import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { MyAccountContainer } from '@components/organisms/MyAccountContainer';
import { Table } from '@components/atoms/Table';
import { Card } from '@components/atoms/Card';
import { Typography, TypographyTagVariants } from '@components/atoms/Typography';
import { Space } from '@components/atoms/Space';
import { Anchor, AnchorMode } from '@components/atoms/Anchor';
import { SITE_URLS } from '@constants';
import { formatPrice } from '@utils/format';

export interface MyAccountPointsHistoryProps {
    points: {
        order_uuid: string;
        order_code: string;
        purchase_value: number;
        purchase_date: string;
        points_earned: number;
    }[];
}

export const MyAccountPointsHistory: FC<MyAccountPointsHistoryProps> = ({ points }) => {
    const { t } = useTranslation();

    const translations = {
        title: t('my-points-component.metatags.title'),
        totalPoints: t('my-points-component.headers.total-points'),
    };

    const totalPoints = points.reduce((sum, point) => sum + point.points_earned, 0);

    return (
        <MyAccountContainer title={translations.title}>
            <div className="flex flex-col">
                <Space spaceBreakpoints={{ xs: { space: 30 } }} />
                <PointsHistoryTable points={points} />
                <div className="p-4 text-end">
                    <Typography fontSize="16" tagVariant={TypographyTagVariants.span} fontWeight={'300'}>
                        <>
                            {translations.totalPoints}: {totalPoints.toString()}
                        </>
                    </Typography>
                </div>
            </div>
        </MyAccountContainer>
    );
};

interface PointsHistoryTableProps {
    points: MyAccountPointsHistoryProps['points'];
}

const PointsHistoryTable: FC<PointsHistoryTableProps> = ({ points }) => {
    const { t } = useTranslation();

    const translations = {
        tableHeaderOrderCode: t('my-points-component.headers.order-code'),
        tableHeaderPurchaseValue: t('my-points-component.headers.purchase-value'),
        tableHeaderPurchaseDate: t('my-points-component.headers.purchase-date'),
        tableHeaderPointsEarned: t('my-points-component.headers.points-earned'),
    };

    return (
        <Card classes="mb-0 w-full">
            <Table
                borderless={true}
                headerCols={[
                    { classes: 'text-left', text: translations.tableHeaderOrderCode },
                    { classes: 'text-left', text: translations.tableHeaderPurchaseValue },
                    { classes: 'text-left', text: translations.tableHeaderPurchaseDate },
                    { classes: 'text-right', text: translations.tableHeaderPointsEarned },
                ]}
                contentRows={points.map((point) => {
                    return [
                        {
                            classes: 'text-left',
                            content: (
                                <Anchor
                                    href={SITE_URLS.myOrderDetail.replace(':uuid', `${point.order_uuid}`)}
                                    mode={AnchorMode.none}>
                                    <Typography fontSize="14" tagVariant={TypographyTagVariants.span}>
                                        {point.order_code}
                                    </Typography>
                                </Anchor>
                            ),
                        },
                        {
                            classes: 'text-left',
                            content: (
                                <Typography fontSize="14" tagVariant={TypographyTagVariants.span}>
                                    {formatPrice(point.purchase_value)}
                                </Typography>
                            ),
                        },
                        {
                            classes: 'text-left',
                            content: (
                                <Typography fontSize="14" tagVariant={TypographyTagVariants.span}>
                                    {point.purchase_date}
                                </Typography>
                            ),
                        },
                        {
                            classes: 'text-right',
                            content: (
                                <Typography fontSize="14" tagVariant={TypographyTagVariants.span}>
                                    {point.points_earned.toString()}
                                </Typography>
                            ),
                        },
                    ];
                })}
            />
        </Card>
    );
};
